import {
  getFinancialPlanQuality,
  IFinancialPlanQuality,
  IFinancialPlanQualityData,
} from 'partner360/features/financialPlanQuality'
import {
  applySobMandatorySettings,
  type ISobMandatoryContext,
} from 'partner360/features/financialPlanQuality'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type { IFinancialCockpitActualsAndTargetsSetting } from '../../api/types'
import type { CockpitPeriodKey, ICockpitCategoryOption, IPartnerFinSobRow } from '../../model/types'
import { findGrowthValue, OVERALL_CATEGORY } from '../revenueAndSob'
import { isActualsFinSobRow } from '../revenueAndSob/isActualsFinSobRow'
import { isCanonicalFinSobRow } from '../revenueAndSob/isCanonicalFinSobRow'
import { isRequestedCategoryTotalRow } from '../revenueAndSob/isRequestedCategoryTotalRow'

const FULL_YEAR_QUARTER = 5

type TMeasureField =
  | 'totalSumNdp'
  | 'totalSumNdpLc'
  | 'totalSumNet'
  | 'totalSumNetLc'
  | 'totalSumTNet'
  | 'totalSumTNetLc'
  | 'totalSumUTNet'
  | 'totalSumUTNetLc'

const USD_FIELD_BY_TYPE: Record<ActualsValueTypes, TMeasureField | null> = {
  [ActualsValueTypes.None]: null,
  [ActualsValueTypes.Ndp]: 'totalSumNdp',
  [ActualsValueTypes.Net]: 'totalSumNet',
  [ActualsValueTypes.TNet]: 'totalSumTNet',
  [ActualsValueTypes.UTNet]: 'totalSumUTNet',
}

const LC_FIELD_BY_TYPE: Record<ActualsValueTypes, TMeasureField | null> = {
  [ActualsValueTypes.None]: null,
  [ActualsValueTypes.Ndp]: 'totalSumNdpLc',
  [ActualsValueTypes.Net]: 'totalSumNetLc',
  [ActualsValueTypes.TNet]: 'totalSumTNetLc',
  [ActualsValueTypes.UTNet]: 'totalSumUTNetLc',
}

interface IWeightedRow {
  row: IPartnerFinSobRow
  weight: number
}

interface IQualityGroup {
  offering: string
  category: string
  rows: IPartnerFinSobRow[]
  hasSelectedPeriod: boolean
}

const getGroupKey = (...parts: string[]): string => parts.join('\0')

const sumMeasure = (rows: IWeightedRow[], field: TMeasureField | null): number => {
  if (field === null) {
    return 0
  }

  return rows.reduce((sum, { row, weight }) => sum + (Number(row[field]) || 0) * weight, 0)
}

const getWeightedSob = (rows: IWeightedRow[], field: TMeasureField | null): number => {
  const rowsWithSob = rows.filter(({ row }) => typeof row.sob === 'number')

  if (rowsWithSob.length === 0) {
    return 0
  }

  if (field === null) {
    const totalWeight = rowsWithSob.reduce((sum, { weight }) => sum + weight, 0)
    return totalWeight
      ? rowsWithSob.reduce((sum, { row, weight }) => sum + row.sob! * weight, 0) / totalWeight
      : 0
  }

  const totalRevenue = sumMeasure(rowsWithSob, field)

  return totalRevenue
    ? rowsWithSob.reduce(
        (sum, { row, weight }) => sum + row.sob! * (Number(row[field]) || 0) * weight,
        0
      ) / totalRevenue
    : getWeightedSob(rowsWithSob, null)
}

const QUARTER_BY_PERIOD: Partial<Record<CockpitPeriodKey, number>> = {
  q1: 1,
  q2: 2,
  q3: 3,
  q4: 4,
}

const getPeriodWeights = (
  periods: CockpitPeriodKey[],
  currentFQuarter: number,
  fQuarterActualsPercentage: number
): Map<number, number> => {
  if (periods.includes('fullYear')) {
    return new Map([[FULL_YEAR_QUARTER, 1]])
  }

  if (periods.includes('ytd')) {
    const weights = new Map<number, number>()
    for (let quarter = 1; quarter <= Math.min(currentFQuarter, 4); quarter += 1) {
      weights.set(quarter, quarter === currentFQuarter ? fQuarterActualsPercentage : 1)
    }
    return weights
  }

  return new Map(
    periods.flatMap((period) => {
      const quarter = QUARTER_BY_PERIOD[period]
      return quarter ? [[quarter, 1] as const] : []
    })
  )
}

interface IGetFinancialPlanQualityFromCockpitRowsParams {
  rows: IPartnerFinSobRow[]
  fYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  offering?: string
  category?: string | null
  periods: CockpitPeriodKey[]
  currentFQuarter: number
  fQuarterActualsPercentage: number
  offeringPresentation?: Array<{ offering: string; offeringName: string }>
  categoriesByOffering?: Partial<Record<string, ICockpitCategoryOption[]>>
  sobMandatoryContext?: ISobMandatoryContext
  settings?: IFinancialCockpitActualsAndTargetsSetting[]
}

/** Builds FinancialPlanQuality from category-total planning rows in the cockpit's selected period.
 * Multiple partner rows are combined before scoring; revenue is summed and SOB is revenue-weighted. */
export const getFinancialPlanQualityFromCockpitRows = ({
  rows,
  fYear,
  actualsType,
  isLocalCurrency,
  offering,
  category,
  periods,
  currentFQuarter,
  fQuarterActualsPercentage,
  offeringPresentation = [],
  categoriesByOffering = {},
  settings = [],
  sobMandatoryContext,
}: IGetFinancialPlanQualityFromCockpitRowsParams): IFinancialPlanQuality => {
  const measureField = (isLocalCurrency ? LC_FIELD_BY_TYPE : USD_FIELD_BY_TYPE)[actualsType]
  const periodWeights = getPeriodWeights(periods, currentFQuarter, fQuarterActualsPercentage)
  const canonicalRows = rows
    .filter(isCanonicalFinSobRow)
    .filter((row) => !offering || row.offering.trim() === offering.trim())
    .filter((row) => !category || row.category === category)
  const scopedRows = canonicalRows.filter(
    (row) =>
      row.category !== OVERALL_CATEGORY &&
      row.subCategory === OVERALL_CATEGORY &&
      (!row.l3Category || row.l3Category === OVERALL_CATEGORY) &&
      isRequestedCategoryTotalRow(row, row.category)
  )
  const offeringOrder = new Map(
    offeringPresentation.map(({ offering: offeringKey }, index) => [offeringKey.trim(), index])
  )
  const offeringNames = new Map(
    offeringPresentation.map(({ offering: offeringKey, offeringName }) => [
      offeringKey.trim(),
      offeringName,
    ])
  )
  const categoryOrder = new Map(
    Object.entries(categoriesByOffering).flatMap(([offeringKey, categoryOptions]) =>
      (categoryOptions ?? []).map(({ category: categoryKey, order }) => [
        getGroupKey(offeringKey.trim(), categoryKey),
        order,
      ])
    )
  )
  const subCategoryOrder = new Map<string, number>()
  const sobAvailability = new Map<string, boolean>()
  settings
    .filter(({ IsMainHierarchy }) => IsMainHierarchy === 1)
    .forEach(({ ProgramType, Category, SubCategory, SubCategoryOrder, IsSoBAvailable }) => {
      const subCategoryKey = getGroupKey(ProgramType.trim(), Category.trim(), SubCategory.trim())
      const currentOrder = subCategoryOrder.get(subCategoryKey)

      sobAvailability.set(subCategoryKey, IsSoBAvailable === 1)

      if (
        SubCategory &&
        SubCategory !== OVERALL_CATEGORY &&
        (currentOrder === undefined || SubCategoryOrder < currentOrder)
      ) {
        subCategoryOrder.set(subCategoryKey, SubCategoryOrder)
      }
    })
  const groupsByKey = new Map<string, IQualityGroup>()

  scopedRows.forEach((row) => {
    const rowOffering = row.offering.trim()
    const key = getGroupKey(rowOffering, row.category)
    const group = groupsByKey.get(key) ?? {
      offering: rowOffering,
      category: row.category,
      rows: [],
      hasSelectedPeriod: false,
    }

    group.rows.push(row)
    group.hasSelectedPeriod ||= periodWeights.has(row.fQuarter)
    groupsByKey.set(key, group)
  })

  const subCategoryRowsByGroup = new Map<string, Map<string, IPartnerFinSobRow[]>>()
  canonicalRows.forEach((row) => {
    const subCategory = row.subCategory?.trim()
    if (!subCategory || subCategory === OVERALL_CATEGORY || row.l3Category !== OVERALL_CATEGORY) {
      return
    }

    const groupKey = getGroupKey(row.offering.trim(), row.category)
    const rowsBySubCategory = subCategoryRowsByGroup.get(groupKey) ?? new Map()
    const subCategoryRows = rowsBySubCategory.get(subCategory) ?? []

    subCategoryRows.push(row)
    rowsBySubCategory.set(subCategory, subCategoryRows)
    subCategoryRowsByGroup.set(groupKey, rowsBySubCategory)
  })

  const groups = Array.from(groupsByKey.values()).filter(
    ({ hasSelectedPeriod }) => hasSelectedPeriod
  )

  const data: IFinancialPlanQualityData[] = groups.map(
    ({ offering: rowOffering, category: rowCategory, rows: categoryRows }) => {
      const buildData = (
        nodeRows: IPartnerFinSobRow[],
        id: string,
        label: string
      ): IFinancialPlanQualityData => {
        const periodWeightedRows = nodeRows
          .filter((row) => periodWeights.has(row.fQuarter))
          .map((row) => ({ row, weight: periodWeights.get(row.fQuarter)! }))
        const planRows = periodWeightedRows.filter(({ row }) => row.fYear === fYear && row.isTarget)
        const baselineRows = periodWeightedRows.filter(
          ({ row }) => row.fYear === fYear - 1 && isActualsFinSobRow(row)
        )
        const selectedPeriodCurrentYearRows = periodWeightedRows
          .map(({ row }) => row)
          .filter((row) => row.fYear === fYear)
        const annualCurrentYearRows = nodeRows.filter(
          (row) => row.fYear === fYear && row.fQuarter === FULL_YEAR_QUARTER
        )

        return {
          id,
          category: label,
          plan: sumMeasure(planRows, measureField),
          baseline: sumMeasure(baselineRows, measureField),
          sobPlan: getWeightedSob(planRows, measureField),
          sobBaseline: getWeightedSob(baselineRows, measureField),
          finYoyGrowth:
            findGrowthValue(selectedPeriodCurrentYearRows, 'finYoyGrowth') ??
            findGrowthValue(annualCurrentYearRows, 'finYoyGrowth'),
          sobYoyGrowth:
            findGrowthValue(selectedPeriodCurrentYearRows, 'sobYoyGrowth') ??
            findGrowthValue(annualCurrentYearRows, 'sobYoyGrowth'),
        }
      }

      const id = getGroupKey(rowOffering, rowCategory)
      const parent: IFinancialPlanQualityData = {
        ...buildData(categoryRows, id, rowCategory),
        offering: offering ? undefined : (offeringNames.get(rowOffering) ?? rowOffering),
        offeringOrder: offeringOrder.get(rowOffering),
        order: categoryOrder.get(getGroupKey(rowOffering, rowCategory)),
      }
      const categorySobAvailability = sobAvailability.get(
        getGroupKey(rowOffering, rowCategory, OVERALL_CATEGORY)
      )
      if (categorySobAvailability !== undefined) {
        parent.isSobAvailable = categorySobAvailability
      }
      const partnerIds = [...new Set(categoryRows.map((row) => row.partnerId).filter(Boolean))]

      if (partnerIds.length === 1) {
        parent.planningLink = {
          partnerId: partnerIds[0],
          fYear,
          programType: rowOffering,
          category: rowCategory,
        }
      }
      const isExpanded = categoryRows.some(
        (row) => row.fYear === fYear && row.isTarget && row.isCollapsed === false
      )

      if (isExpanded) {
        const rowsBySubCategory = subCategoryRowsByGroup.get(id) ?? new Map()
        const children = Array.from(rowsBySubCategory, ([subCategory, subCategoryRows]) => {
          const child: IFinancialPlanQualityData = {
            ...buildData(subCategoryRows, getGroupKey(id, subCategory), subCategory),
            order: subCategoryOrder.get(getGroupKey(rowOffering, rowCategory, subCategory)),
          }
          const childSobAvailability = sobAvailability.get(
            getGroupKey(rowOffering, rowCategory, subCategory)
          )
          if (childSobAvailability !== undefined) {
            child.isSobAvailable = childSobAvailability
          }

          return child
        })

        if (children.length) parent.children = children
      }

      return applySobMandatorySettings(
        [parent],
        settings,
        sobMandatoryContext ?? { partnerFlags: '', sectionSettings: {} },
        rowOffering
      )[0]
    }
  )

  return getFinancialPlanQuality(data)
}
